/*
 * Shared labels, colors and helpers for the Ocean Shipment Status screens.
 */

export const STANDARD_EVENTS = [
    ["BOOKING_CONFIRMED", "Booking Confirmed"],
    ["EMPTY_RELEASED", "Empty Released"],
    ["EMPTY_PICKED_UP", "Empty Picked Up"],
    ["GATE_IN", "Gate In"],
    ["LOADED_ON_VESSEL", "Loaded on Vessel"],
    ["VESSEL_DEPARTED", "Vessel Departed"],
    ["TRANSSHIPMENT_ARRIVAL", "Transshipment Arrival"],
    ["TRANSSHIPMENT_DEPARTURE", "Transshipment Departure"],
    ["VESSEL_ARRIVED", "Vessel Arrived"],
    ["DISCHARGED", "Discharged"],
    ["GATE_OUT", "Gate Out"],
    ["EMPTY_RETURNED", "Empty Returned"],
];
export const STANDARD_LABEL = Object.fromEntries(STANDARD_EVENTS);
export const STANDARD_RANK = Object.fromEntries(STANDARD_EVENTS.map(([code], i) => [code, i]));

export const CARRIER_COLORS = {
    MSC: "#f2c200",
    MAERSK: "#42b0d5",
    CMA_CGM: "#0b2b5c",
    HAPAG_LLOYD: "#f26c00",
    ONE: "#c0007a",
};

export const CARRIER_SHORT = {
    MSC: "MSC",
    MAERSK: "Maersk",
    CMA_CGM: "CMA CGM",
    HAPAG_LLOYD: "Hapag-Lloyd",
    ONE: "ONE",
};

export const TONE = {
    blue: { color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
    green: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
    amber: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
    red: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
    slate: { color: "#475569", bg: "#f8fafc", border: "#e2e8f0" },
};

const DAY = 24 * 60 * 60 * 1000;

export const arrivalOf = (t) => t.ata || t.eta || null;

// One status per row, ordered by what an operator must look at first.
export const rowState = (t) => {
    if (["ERROR", "NOT_FOUND", "NOT_CONFIGURED"].includes(t.trackingStatus)) {
        return {
            key: "attention",
            label: { ERROR: "Carrier API error", NOT_FOUND: "Not found at carrier", NOT_CONFIGURED: "API not connected" }[
                t.trackingStatus
            ],
            tone: t.trackingStatus === "NOT_CONFIGURED" ? "slate" : "red",
        };
    }
    if (t.trackingStatus === "PAUSED") return { key: "paused", label: "Paused", tone: "slate" };
    if (t.trackingStatus === "COMPLETED") return { key: "completed", label: "Completed", tone: "green" };
    if (t.exceptionStatus === "EXCEPTION") return { key: "exception", label: "Exception", tone: "red" };
    if (t.exceptionStatus === "DELAYED") return { key: "delayed", label: "Delayed", tone: "amber" };
    if (t.trackingStatus === "PENDING") return { key: "pending", label: "Waiting for carrier", tone: "slate" };
    return { key: "transit", label: "In transit", tone: "blue" };
};

// "5 days left", "arrived 2 days ago", "2 days late"
export const arrivalHint = (t) => {
    if (t.ata) {
        const days = Math.round((Date.now() - new Date(t.ata).getTime()) / DAY);
        return { text: days <= 0 ? "Arrived today" : `Arrived ${days}d ago`, tone: "green" };
    }
    if (!t.eta) return null;
    const diff = Math.round((new Date(t.eta).getTime() - Date.now()) / DAY);
    if (diff < 0) return { text: `${-diff}d overdue`, tone: "red" };
    if (diff === 0) return { text: "Due today", tone: "amber" };
    return { text: `in ${diff}d`, tone: diff <= 7 ? "blue" : "slate" };
};

export const arrivingWithinDays = (t, days) => {
    if (t.ata || !t.eta || t.trackingStatus === "COMPLETED") return false;
    const diff = new Date(t.eta).getTime() - Date.now();
    return diff >= -DAY && diff <= days * DAY;
};

export const portText = (port) => port?.locode || port?.name || "";

/*
 * All tracking times are stored in UTC and always SHOWN in Türkiye time,
 * whatever time zone the user's computer is set to.
 */
export const TZ = "Europe/Istanbul";
export const TZ_LABEL = "TRT (UTC+3)";

export const formatDate = (value, withTime = false) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-GB", {
        timeZone: TZ,
        day: "2-digit",
        month: "short",
        year: withTime ? undefined : "numeric",
        ...(withTime ? { hour: "2-digit", minute: "2-digit", hourCycle: "h23" } : {}),
    });
};

// Wall-clock parts of an instant in Türkiye time.
export const istanbulParts = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const parts = Object.fromEntries(
        new Intl.DateTimeFormat("en-GB", {
            timeZone: TZ,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hourCycle: "h23",
        })
            .formatToParts(date)
            .map((p) => [p.type, p.value])
    );
    return { year: +parts.year, month: +parts.month, day: +parts.day, hour: +parts.hour, minute: +parts.minute };
};

const pad2 = (n) => String(n).padStart(2, "0");

// "2026-10-12 09:30" in Türkiye time (exports).
export const istanbulStamp = (value) => {
    const p = value ? istanbulParts(value) : null;
    return p ? `${p.year}-${pad2(p.month)}-${pad2(p.day)} ${pad2(p.hour)}:${pad2(p.minute)}` : "";
};

/*
 * For date pickers + "HH:mm" inputs: a Date whose LOCAL fields equal the
 * Türkiye wall clock, so the picker shows the Türkiye date on any machine.
 */
export const istanbulPickerValue = (value) => {
    const p = value ? istanbulParts(value) : null;
    if (!p) return null;
    return { date: new Date(p.year, p.month - 1, p.day, p.hour, p.minute), time: `${pad2(p.hour)}:${pad2(p.minute)}` };
};

// Start / end of a "YYYY-MM-DD" day in Türkiye time, as epoch ms (filters).
export const istanbulDayStart = (ymd) => (ymd ? new Date(`${ymd}T00:00:00+03:00`).getTime() : null);
export const istanbulDayEnd = (ymd) => (ymd ? new Date(`${ymd}T23:59:59+03:00`).getTime() : null);

export const timeAgo = (value) => {
    if (!value) return "never";
    const minutes = Math.round((Date.now() - new Date(value).getTime()) / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
};

export const errorText = (error, fallback) => error?.errors?.[0]?.detail || error?.message || fallback;
